import { api } from '../../api/client';

export type LocationKind = 'room' | 'cabinet' | 'shelf' | 'box' | 'compartment';

export const LOCATION_KINDS: LocationKind[] = [
  'room',
  'cabinet',
  'shelf',
  'box',
  'compartment',
];

export interface LocationBrief {
  id: string;
  code: string;
  name: string;
  kind: LocationKind;
}

/** A search result carries its ancestor chain: names repeat, so the name alone
 *  does not identify anything. */
export interface LocationSearchHit extends LocationBrief {
  ancestors: string[];
}

export interface LocationNode extends LocationBrief {
  parent_id: string | null;
  depth: number;
  sort_order: number;
  has_children: boolean;
  primary_image_id: string | null;
}

export interface FileBrief {
  id: string;
  original_filename: string;
  mime: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
}

export interface LocationImage {
  id: string;
  file_id: string;
  caption: string | null;
  is_primary: boolean;
  sort_order: number;
  file: FileBrief;
}

export type AnchorShape = 'point' | 'rect' | 'polygon';

export interface Anchor {
  id: string;
  child_location_id: string;
  parent_file_id: string;
  shape: AnchorShape;
  /** Normalised 0..1 against the image's natural size. */
  x: number;
  y: number;
  /** Present for rectangles, absent for points. */
  w: number | null;
  h: number | null;
  label: string | null;
  child: LocationBrief | null;
}

export interface LocationDetail extends LocationBrief {
  parent_id: string | null;
  path: string;
  depth: number;
  description: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  breadcrumbs: LocationBrief[];
  images: LocationImage[];
  children_count: number;
}

export interface LocationInput {
  /** Omit to have the server generate one. */
  code?: string;
  name: string;
  kind: LocationKind;
  description?: string | null;
  parent_id?: string | null;
}

export const listChildren = (parentId: string | null) =>
  api.get<LocationNode[]>(`/locations${parentId ? `?parent_id=${parentId}` : ''}`);

export const getLocation = (id: string) => api.get<LocationDetail>(`/locations/${id}`);

export const searchLocations = (query: string) =>
  api.get<LocationSearchHit[]>(`/locations/search?q=${encodeURIComponent(query)}`);

/** The full set of a parent's children, in the order wanted. */
export const reorderLocations = (parentId: string | null, orderedIds: string[]) =>
  api.post<void>('/locations/reorder', { parent_id: parentId, ordered_ids: orderedIds });

export const createLocation = (input: LocationInput) =>
  api.post<LocationDetail>('/locations', input);

export const updateLocation = (id: string, input: Partial<LocationInput>) =>
  api.patch<LocationDetail>(`/locations/${id}`, input);

export const moveLocation = (id: string, parentId: string | null) =>
  api.post<LocationDetail>(`/locations/${id}/move`, { parent_id: parentId });

export const archiveLocation = (id: string) => api.delete<void>(`/locations/${id}`);

export const uploadFile = (file: File) => api.upload<FileBrief>('/files', file);

export const attachImage = (locationId: string, fileId: string) =>
  api.post<LocationImage>(`/locations/${locationId}/images?file_id=${fileId}`);

export const setPrimaryImage = (locationId: string, imageId: string) =>
  api.post<LocationImage>(`/locations/${locationId}/images/${imageId}/primary`);

export const detachImage = (locationId: string, imageId: string) =>
  api.delete<void>(`/locations/${locationId}/images/${imageId}`);

export const listAnchors = (fileId: string) =>
  api.get<Anchor[]>(`/locations/files/${fileId}/anchors`);

export interface AnchorInput {
  child_location_id: string;
  shape: AnchorShape;
  x: number;
  y: number;
  /** Signed: the editor reports the drag direction, the server normalises it. */
  w?: number;
  h?: number;
  label?: string | null;
}

export const setAnchor = (fileId: string, body: AnchorInput) =>
  api.put<Anchor>(`/locations/files/${fileId}/anchors`, body);

export const deleteAnchor = (anchorId: string) =>
  api.delete<void>(`/locations/anchors/${anchorId}`);

/* A photograph is not addressed by URL any more: files are served by the API and
   need the session, and in the Android application that session is a header no
   `<img>` can carry. `components/ui/FileImage` is how one is shown. */
